import { getAccessToken } from './authStorage';

/**
 * 转为同源路径，便于走 devServer 代理并触发下载
 * @param {string} url
 */
export function resolveDownloadUrl(url) {
  if (!url) {
    return url;
  }
  if (url.startsWith('/')) {
    return url;
  }
  try {
    const parsed = new URL(url, window.location.origin);
    if (parsed.pathname.startsWith('/api/')) {
      return `${parsed.pathname}${parsed.search}`;
    }
  } catch (e) {
    // ignore invalid url
  }
  return url;
}

/**
 * 触发单个文件下载（fetch blob，避免浏览器直接播放）
 * @param {string} url
 * @param {string} filename
 */
export async function downloadFile(url, filename) {
  const fetchUrl = resolveDownloadUrl(url);
  const headers = {};
  const token = getAccessToken();
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  const response = await fetch(fetchUrl, { headers });
  if (!response.ok) {
    throw new Error(`下载失败 HTTP ${response.status}`);
  }
  const blob = await response.blob();
  const blobUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = blobUrl;
  link.download = filename || 'video.mp4';
  link.rel = 'noopener';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(blobUrl);
}

/**
 * 从 Content-Disposition 解析文件名
 * @param {string|null} disposition
 */
export function parseFilenameFromDisposition(disposition) {
  if (!disposition) {
    return null;
  }
  const utf8Match = disposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match) {
    try {
      return decodeURIComponent(utf8Match[1].trim());
    } catch (e) {
      // ignore decode error
    }
  }
  const asciiMatch = disposition.match(/filename="([^"]+)"/i);
  if (asciiMatch) {
    return asciiMatch[1];
  }
  return null;
}

/**
 * 批量下载切片 ZIP（后端打包，一次保存）
 * @param {string} taskId
 * @param {string[]} segmentIds
 * @param {string} fallbackFileName
 */
export async function downloadSegmentBatchZip(taskId, segmentIds = [], fallbackFileName = 'segments.zip') {
  return downloadTaskArchive(
    `/api/segment-tasks/${encodeURIComponent(taskId)}/download`,
    segmentIds,
    fallbackFileName,
  );
}

/**
 * 下载发布素材包 ZIP
 * @param {string} taskId
 * @param {string[]} segmentIds
 * @param {string} fallbackFileName
 */
export async function downloadPublishPack(taskId, segmentIds = [], fallbackFileName = 'publish_pack.zip') {
  return downloadTaskArchive(
    `/api/segment-tasks/${encodeURIComponent(taskId)}/publish-pack`,
    segmentIds,
    fallbackFileName,
  );
}

async function downloadTaskArchive(baseUrl, segmentIds = [], fallbackFileName = 'archive.zip') {
  if (!baseUrl) {
    throw new Error('缺少下载地址');
  }
  const params = new URLSearchParams();
  if (segmentIds.length) {
    params.set('ids', segmentIds.join(','));
  }
  const query = params.toString();
  const url = `${baseUrl}${query ? `?${query}` : ''}`;
  const headers = {};
  const token = getAccessToken();
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  const response = await fetch(url, { headers });
  if (!response.ok) {
    const text = await response.text();
    let message = text || `下载失败 HTTP ${response.status}`;
    try {
      const payload = JSON.parse(text);
      if (payload && payload.message) {
        message = payload.message;
      }
    } catch (e) {
      // not json
    }
    throw new Error(message);
  }
  const blob = await response.blob();
  const filename = parseFilenameFromDisposition(response.headers.get('Content-Disposition'))
    || fallbackFileName;
  const blobUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = blobUrl;
  link.download = filename;
  link.rel = 'noopener';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(blobUrl);
  return { count: segmentIds.length || 1, fileName: filename };
}

/**
 * 批量下载（顺序触发，Mock 模式或降级方案）
 * @param {{ url: string, fileName?: string, name?: string }[]} items
 * @param {number} delayMs
 */
export async function downloadFilesBatch(items, delayMs = 400) {
  if (!items || !items.length) {
    return { count: 0 };
  }
  for (let i = 0; i < items.length; i += 1) {
    const item = items[i];
    const name = item.fileName || item.name || `segment-${i + 1}.mp4`;
    await downloadFile(item.url, name);
    if (i < items.length - 1) {
      await new Promise(resolve => setTimeout(resolve, delayMs));
    }
  }
  return { count: items.length };
}
